import { useState, useEffect, useCallback } from 'react';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import { Collection } from '@/lib/types';

interface Props {
  initialized: boolean;
  collectionTypeId?: string;
  filters?: any[];
  sortKey: string;
  searchQuery: string;
  pageNumber: number;
}

const useCollectionData = ({
  initialized,
  collectionTypeId,
  filters,
  sortKey,
  searchQuery,
  pageNumber,
}: Props) => {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [reload, setReload] = useState(false);

  const fetchData = useCallback(async () => {
    if (initialized === false || !collectionTypeId) {
      setCollections([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      let sort: string[] = [];
      if (sortKey === 'recent') {
        sort = ['created_at:desc'];
      } else if (sortKey === 'titleAsc') {
        sort = ['name:asc'];
      } else if (sortKey === 'titleDesc') {
        sort = ['name:desc'];
      }

      const response = await api.post(`collections/search`, {
        q: searchQuery,
        page: pageNumber,
        page_size: 10,
        sort: sort,
        filters: filters,
        collection_type_id: parseInt(collectionTypeId, 10),
      });

      const stored = localStorage.getItem('deletedCollectionIds');
      const deletedCollectionIds: string[] = stored ? JSON.parse(stored) : [];

      const hits = response.data.hits.filter(
        (collection: { id: string }) => !deletedCollectionIds.includes(collection.id),
      );

      if (pageNumber === 1) {
        setCollections([]);
      }
      setCollections((prevContents) => [...prevContents, ...hits]);
      setHasMore(response.data.total_pages > response.data.current_page);
      setIsLoading(false);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to get collections',
      });
      setIsLoading(false);
    }
  }, [initialized, collectionTypeId, filters, pageNumber, sortKey, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData, reload]);

  const reloadCollections = () => {
    setReload((prev) => !prev);
  };

  return {
    collections,
    isLoading,
    hasMore,
    reloadCollections,
  };
};

export default useCollectionData;
